import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Candidate, AssessmentTemplate, PortalRole, PortalUser } from '../types';

// ── Icons ─────────────────────────────────────────────────────────────────────
interface IconProps { name: string; size?: number; color?: string; }
const Icon: React.FC<IconProps> = ({ name, size = 16, color = "currentColor" }) => {
  const icons: Record<string, React.ReactNode> = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></>,
    candidates: <><circle cx="9" cy="7" r="4"/><path d="M2 21v-1a7 7 0 0 1 14 0v1"/><circle cx="19" cy="11" r="3"/><path d="M22 20v-.5a4.5 4.5 0 0 0-6-4.24"/></>,
    results: <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2"/></>,
    profile: <><circle cx="12" cy="8" r="5"/><path d="M3 21v-1a9 9 0 0 1 18 0v1"/></>,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    chevronDown: <><polyline points="6 9 12 15 18 9"/></>,
    chevronRight: <><polyline points="9 6 15 12 9 18"/></>,
    plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    search: <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    filter: <><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>,
    bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
    copy: <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
    check: <><polyline points="20 6 9 12 4 9"/></>,
    x: <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    code: <><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></>,
    trending: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>,
    alert: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    bar: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    key: <><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></>,
    database: <><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></>,
    cpu: <><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></>,
    eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    send: <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
    edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash: <><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></>,
    refresh: <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

// ── Markdown Preview ─────────────────────────────────────────────────────────
const MarkdownPreview: React.FC<{ src: string }> = ({ src }) => {
  const html = src
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.+)$/gm, '<h3 style="font-size:14px;font-weight:700;margin:14px 0 4px">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="font-size:15px;font-weight:700;margin:16px 0 6px">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="font-size:17px;font-weight:700;margin:0 0 8px">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="font-family:JetBrains Mono,monospace;font-size:12px;background:var(--bg-3);padding:1px 5px;border-radius:4px">$1</code>')
    .replace(/^```[\w]*\n?([\s\S]*?)```$/gm, '<pre style="background:var(--bg-3);padding:12px;border-radius:6px;font-family:JetBrains Mono,monospace;font-size:12px;overflow-x:auto;margin:8px 0"><code>$1</code></pre>')
    .replace(/^- (.+)$/gm, '<li style="margin:2px 0;padding-left:4px">$1</li>')
    .replace(/(<li[\s\S]*?<\/li>\n?)+/g, '<ul style="margin:6px 0;padding-left:20px">$&</ul>')
    .replace(/\n\n/g, '</p><p style="margin:6px 0">')
    .replace(/^(?!<[hpulpre])(.+)$/gm, '<p style="margin:4px 0">$1</p>');
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

// ── Badge ─────────────────────────────────────────────────────────────────────
interface BadgeProps { children: React.ReactNode; variant?: string; }
const Badge: React.FC<BadgeProps> = ({ children, variant = "default" }) => {
  const variants: Record<string, { bg: string; color: string }> = {
    default: { bg: "var(--bg-3)", color: "var(--text-2)" },
    accent: { bg: "var(--accent-light)", color: "var(--accent)" },
    green: { bg: "var(--green-light)", color: "var(--green)" },
    amber: { bg: "var(--amber-light)", color: "var(--amber)" },
    red: { bg: "var(--red-light)", color: "var(--red)" },
    teal: { bg: "var(--teal-light)", color: "var(--teal)" },
  };
  const v = variants[variant] || variants.default;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 99, fontSize: 12, fontWeight: 500, background: v.bg, color: v.color }}>
      {children}
    </span>
  );
};

// ── Avatar ────────────────────────────────────────────────────────────────────
interface AvatarProps { name: string; size?: number; color?: string; }
const Avatar: React.FC<AvatarProps> = ({ name, size = 32, color = "var(--accent)" }) => {
  const initials = name.split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: 99, background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 600, color: "#fff", flexShrink: 0, letterSpacing: "-0.5px" }}>
      {initials}
    </div>
  );
};

// ── Button ────────────────────────────────────────────────────────────────────
interface BtnProps {
  children?: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  onClick?: () => void;
  icon?: string;
  small?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}
const Btn: React.FC<BtnProps> = ({ children, variant = "primary", onClick, icon, small, disabled, type = "button" }) => {
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: "var(--accent)", color: "#fff", border: "none" },
    secondary: { background: "#fff", color: "var(--text)", border: "1px solid var(--border)" },
    ghost: { background: "transparent", color: "var(--text-2)", border: "none" },
    danger: { background: "var(--red-light)", color: "var(--red)", border: "1px solid var(--red-light)" },
  };
  const s = styles[variant] || styles.primary;
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...s, display: "inline-flex", alignItems: "center", gap: 6, padding: small ? "6px 12px" : "8px 16px", borderRadius: "var(--radius-sm)", fontSize: small ? 12.5 : 13.5, fontWeight: 600, cursor: "pointer", transition: "all 0.12s", opacity: disabled ? 0.5 : 1, fontFamily: "inherit" }}>
      {icon && <Icon name={icon} size={small ? 13 : 15} />}
      {children}
    </button>
  );
};

// ── Card ──────────────────────────────────────────────────────────────────────
interface CardProps { children: React.ReactNode; style?: React.CSSProperties; }
const Card: React.FC<CardProps> = ({ children, style }) => (
  <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "var(--shadow-sm)", ...style }}>
    {children}
  </div>
);

// ── Stat Card ─────────────────────────────────────────────────────────────────
interface StatCardProps { label: string; value: string; sub?: string; icon: string; color: string; trend?: string; }
const StatCard: React.FC<StatCardProps> = ({ label, value, sub, icon, color, trend }) => (
  <Card style={{ padding: "20px 24px", flex: 1 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-1px", color: "var(--text)", marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
        {sub && <div style={{ fontSize: 12.5, color: trend === "up" ? "var(--green)" : trend === "down" ? "var(--red)" : "var(--text-3)", marginTop: 4, fontWeight: 500 }}>{sub}</div>}
      </div>
      <div style={{ width: 40, height: 40, borderRadius: "var(--radius)", background: color + "26", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={18} color={color} />
      </div>
    </div>
  </Card>
);

// ── Nav items ─────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard", roles: ["recruiter", "admin", "superadmin"] },
  { id: "candidates", label: "Candidates", icon: "candidates", roles: ["recruiter", "admin", "superadmin"] },
  { id: "results", label: "Results", icon: "results", roles: ["recruiter", "admin", "superadmin"] },
  { id: "assessments", label: "Assessments", icon: "code", roles: ["recruiter", "admin", "superadmin"] },
  { id: "configuration", label: "Configuration", icon: "settings", roles: ["admin", "superadmin"] },
];

// ── Sidebar ───────────────────────────────────────────────────────────────────
interface SidebarProps {
  activeScreen: string;
  onNavigate: (s: string) => void;
  role: PortalRole;
  user: { name: string; email: string };
  onLogout: () => void;
}
const Sidebar: React.FC<SidebarProps> = ({ activeScreen, onNavigate, role, user, onLogout }) => {
  return (
    <aside style={{ width: "var(--sidebar-w)", flexShrink: 0, background: "#fff", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0, zIndex: 10 }}>
      {/* Logo */}
      <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 7, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="code" size={15} color="#fff" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: "-0.3px", color: "var(--text)" }}>TuringScout</div>
            <div style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 500, letterSpacing: "0.4px", textTransform: "uppercase" }}>Recruiter Portal</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 10px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
        {NAV_ITEMS.filter(item => item.roles.includes(role)).map(item => {
          const isActive = activeScreen === item.id;
          return (
            <button key={item.id} onClick={() => onNavigate(item.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: "var(--radius-sm)", fontWeight: 500, fontSize: 13.5, textAlign: "left", width: "100%", transition: "all 0.12s", background: isActive ? "var(--accent-light)" : "transparent", color: isActive ? "var(--accent)" : "var(--text-2)", cursor: "pointer", border: "none", fontFamily: "inherit" }}>
              <Icon name={item.icon} size={16} color={isActive ? "var(--accent)" : "var(--text-3)"} />
              {item.label}
            </button>
          );
        })}

        <div style={{ margin: "8px 0", borderTop: "1px solid var(--border)" }} />

        <button onClick={() => onNavigate("profile")}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: "var(--radius-sm)", fontWeight: 500, fontSize: 13.5, textAlign: "left", width: "100%", background: activeScreen === "profile" ? "var(--accent-light)" : "transparent", color: activeScreen === "profile" ? "var(--accent)" : "var(--text-2)", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
          <Icon name="profile" size={16} color={activeScreen === "profile" ? "var(--accent)" : "var(--text-3)"} />
          My Account
        </button>
      </nav>

      {/* User footer */}
      <div style={{ padding: "12px 14px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
        <Avatar name={user.name} size={30} color={role === "superadmin" ? "var(--teal)" : role === "admin" ? "oklch(55% 0.18 295)" : "var(--accent)"} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", textTransform: "capitalize" }}>{role === "superadmin" || role === "admin" ? "Admin" : "Recruiter"}</div>
        </div>
        <button onClick={onLogout} style={{ color: "var(--text-3)", padding: 4, borderRadius: 4, background: "none", border: "none", cursor: "pointer" }}>
          <Icon name="logout" size={14} />
        </button>
      </div>
    </aside>
  );
};

// ── Top Header ────────────────────────────────────────────────────────────────
interface HeaderProps { title: string; subtitle?: string; actions?: React.ReactNode; }
const Header: React.FC<HeaderProps> = ({ title, subtitle, actions }) => (
  <div style={{ padding: "24px 32px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.4px", color: "var(--text)" }}>{title}</h1>
      {subtitle && <p style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 3 }}>{subtitle}</p>}
    </div>
    {actions && <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>{actions}</div>}
  </div>
);

// ── Status Dot ────────────────────────────────────────────────────────────────
const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  return <Badge variant={status === "completed" ? "green" : status === "in_progress" ? "amber" : "default"}>
    {status === "completed" ? "Completed" : status === "in_progress" ? "In Progress" : "Pending"}
  </Badge>;
};

const ALL_TEMPLATES: AssessmentTemplate[] = [];

const AVATAR_COLORS = ["var(--teal)", "oklch(55% 0.18 295)", "var(--amber)", "var(--accent)", "var(--red)", "oklch(55% 0.18 180)"];
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function fmtDuration(started?: string, completed?: string): string {
  if (!started || !completed) return "—";
  const s = Math.max(0, Math.floor((new Date(completed).getTime() - new Date(started).getTime()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normaliseSession(s: any): Candidate {
  const grade = s.grade ?? null;
  return {
    id: s.session_id,
    name: s.candidate_name ?? "Unknown",
    role: s.role ?? s.challenge_title ?? "—",
    status: s.status === "active" ? "in_progress" : (s.status as Candidate["status"]) ?? "pending",
    score: grade?.total_score != null ? Number(grade.total_score) : (s.score != null ? Number(s.score) : null),
    time: fmtDuration(s.started_at, s.completed_at),
    date: fmtDate(s.created_at),
    avatar_color: avatarColor(s.candidate_name ?? ""),
    grade,
  };
}

interface DashStats { total: number; completed: number; active: number; avg_score: number | null; avg_time: string | null; }

// ── Invite Modal ──────────────────────────────────────────────────────────────
interface InviteModalProps {
  onClose: () => void;
  onSent: (session: Candidate) => void;
  preselectedTemplate?: AssessmentTemplate | null;
  templates: AssessmentTemplate[];
}
const InviteModal: React.FC<InviteModalProps> = ({ onClose, onSent, preselectedTemplate, templates }) => {
  const [form, setForm] = useState({ name: "", email: "", role: "", templateId: preselectedTemplate?.id ?? "" });
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState("");

  const selectedTpl = preselectedTemplate ?? templates.find(t => t.id === form.templateId) ?? templates[0];

  const handleSend = async () => {
    if (!selectedTpl) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/sessions`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          candidate_name: form.name.trim(),
          candidate_email: form.email.trim().toLowerCase(),
          role: form.role.trim(),
          template_id: selectedTpl.id,
          challenge_title: selectedTpl.name,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const session = await res.json();
      setLink(session.invite_url ?? "");
      onSent(normaliseSession(session));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const sent = !!link;

  return (
    <div style={{ position: "fixed", inset: 0, background: "oklch(0% 0 0 / 0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "var(--shadow-lg)", width: 520, maxWidth: "calc(100vw - 32px)", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)" }}>Add New Candidate</div>
            <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2 }}>{sent ? "Invitation sent successfully" : `Step ${step} of 2`}</div>
          </div>
          <button onClick={onClose} style={{ padding: 6, borderRadius: 6, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer" }}><Icon name="x" size={16} /></button>
        </div>

        {sent ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 99, background: "var(--green-light)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="check" size={24} color="var(--green)" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 17, color: "var(--text)" }}>Invitation Sent!</div>
            <div style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 6, marginBottom: 24 }}>
              Share this link with <strong>{form.name}</strong> to begin their assessment.
            </div>
            <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
              <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: "var(--text-2)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link}</span>
              <button onClick={() => navigator.clipboard.writeText(link)} style={{ padding: 4, color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}><Icon name="copy" size={14} /></button>
            </div>
            <Btn onClick={onClose}>Done</Btn>
          </div>
        ) : (
          <div style={{ padding: "24px" }}>
            {step === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>Candidate Full Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Alex Johnson" autoFocus
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>Email Address</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="candidate@email.com" type="email"
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>Role / Position Applied For</label>
                  <input value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    placeholder="e.g. Senior Backend Engineer"
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
                </div>
              </div>
            )}
            {step === 2 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {preselectedTemplate ? (
                  <div style={{ background: "var(--accent-light)", border: "1px solid oklch(85% 0.08 265)", borderRadius: "var(--radius-sm)", padding: "12px 14px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 4 }}>Assessment Preselected</div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{preselectedTemplate.name}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2 }}>{preselectedTemplate.category} · {preselectedTemplate.level} · Difficulty {preselectedTemplate.difficulty}/5</div>
                  </div>
                ) : (
                  <div>
                    <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>Select Assessment Template</label>
                    <select value={form.templateId} onChange={e => setForm(f => ({ ...f, templateId: e.target.value }))}
                      style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }}>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ background: "var(--accent-light)", border: "1px solid oklch(85% 0.08 265)", borderRadius: "var(--radius-sm)", padding: "12px 14px", fontSize: 12.5, color: "var(--accent)" }}>
                  <strong>Inviting:</strong> {form.name} &nbsp;·&nbsp; {form.email} &nbsp;·&nbsp; {form.role || "Unspecified role"}
                </div>
                {error && <div style={{ fontSize: 13, color: "var(--red)", padding: "8px 12px", background: "var(--red-light)", borderRadius: "var(--radius-sm)" }}>{error}</div>}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, gap: 8 }}>
              {step === 1 ? (
                <><div /><Btn onClick={() => setStep(2)} disabled={!form.name || !form.email || !form.role}>Continue →</Btn></>
              ) : (
                <><Btn variant="secondary" onClick={() => setStep(1)}>← Back</Btn><Btn icon="send" onClick={handleSend} disabled={sending || !selectedTpl}>{sending ? "Sending…" : "Send Invitation"}</Btn></>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Dashboard Screen ──────────────────────────────────────────────────────────
interface DashboardScreenProps { onInvite: () => void; onViewResult: (c: Candidate) => void; stats: DashStats | null; sessions: Candidate[]; }
const DashboardScreen: React.FC<DashboardScreenProps> = ({ onInvite, onViewResult, stats, sessions }) => {
  const recent = sessions.slice(0, 5);
  return (
    <div style={{ padding: "24px 32px 40px", display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", gap: 16 }}>
        <StatCard label="Completed" value={stats ? String(stats.completed) : "—"} sub={stats ? `${stats.total} total sent` : "No data yet"} icon="results" color="var(--green)" />
        <StatCard label="Active Invites" value={stats ? String(stats.active) : "—"} sub={stats?.active ? "Awaiting candidates" : "None pending"} icon="mail" color="var(--accent)" />
        <StatCard label="Avg. Score" value={stats?.avg_score != null ? String(stats.avg_score) : "—"} sub="Completed assessments" icon="trending" color="var(--teal)" />
        <StatCard label="Avg. Time" value={stats?.avg_time ?? "—"} sub="Per assessment" icon="clock" color="var(--amber)" />
      </div>

      <Card>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>Recent Activity</div>
          <Btn variant="secondary" small icon="plus" onClick={onInvite}>Add New Candidate</Btn>
        </div>
        {recent.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>👋</div>
            <div style={{ fontWeight: 600, fontSize: 15, color: "var(--text)", marginBottom: 6 }}>No candidates yet</div>
            <div style={{ fontSize: 13.5, color: "var(--text-3)", marginBottom: 20 }}>Send your first assessment to get started.</div>
            <Btn icon="plus" onClick={onInvite}>Add New Candidate</Btn>
          </div>
        ) : (
          <div>
            {recent.map((c, i) => (
              <div key={c.id} style={{ padding: "14px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: i < recent.length - 1 ? "1px solid var(--border)" : "none", transition: "background 0.1s", cursor: "default" }}
                onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = "var(--bg)"}
                onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = ""}>
                <Avatar name={c.name} size={36} color={c.avatar_color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text)" }}>{c.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{c.role} · {c.date}</div>
                </div>
                <StatusDot status={c.status} />
                {c.score !== null && (
                  <div style={{ textAlign: "right", minWidth: 60 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 16, color: c.score >= 85 ? "var(--green)" : c.score >= 70 ? "var(--amber)" : "var(--red)" }}>{c.score}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>/ 100</div>
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: "var(--text-3)", minWidth: 52, textAlign: "right" }}>{c.time}</div>
                {c.status === "completed" && (
                  <Btn variant="ghost" small icon="eye" onClick={() => onViewResult(c)}>View</Btn>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

// ── Candidates Screen ─────────────────────────────────────────────────────────
interface CandidatesScreenProps { onInvite: () => void; onViewResult: (c: Candidate) => void; onDelete: (c: Candidate) => void; onReset: (c: Candidate) => void; sessions: Candidate[]; }
const CandidatesScreen: React.FC<CandidatesScreenProps> = ({ onInvite, onViewResult, onDelete, onReset, sessions }) => {
  const [search, setSearch] = useState("");
  const filtered = sessions.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.role.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ padding: "24px 32px 40px", display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <div style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
            <Icon name="search" size={14} color="var(--text-3)" />
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or role…"
            style={{ width: "100%", padding: "8px 12px 8px 34px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "#fff", outline: "none", fontFamily: "inherit" }} />
        </div>
        <Btn variant="secondary" icon="filter" small>Filter</Btn>
        <Btn icon="plus" onClick={onInvite}>Add New Candidate</Btn>
      </div>

      <Card>
        <div style={{ padding: "14px 24px", borderBottom: "1px solid var(--border)", display: "grid", gridTemplateColumns: "2fr 2fr 1fr 80px 80px 130px", gap: 12, fontSize: 11.5, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          <span>Candidate</span><span>Role</span><span>Status</span><span style={{ textAlign: "right" }}>Score</span><span style={{ textAlign: "right" }}>Time</span><span></span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 13.5, color: "var(--text-3)" }}>{sessions.length === 0 ? "No candidates yet. Send your first assessment to get started." : "No candidates match your search."}</div>
            {sessions.length === 0 && <div style={{ marginTop: 16 }}><Btn icon="plus" onClick={onInvite}>Add New Candidate</Btn></div>}
          </div>
        ) : filtered.map((c, i) => (
          <div key={c.id} style={{ padding: "14px 24px", display: "grid", gridTemplateColumns: "2fr 2fr 1fr 80px 80px 130px", gap: 12, alignItems: "center", borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none", cursor: "default" }}
            onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = "var(--bg)"}
            onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = ""}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar name={c.name} size={30} color={c.avatar_color} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text)" }}>{c.name}</div>
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>{c.date}</div>
              </div>
            </div>
            <span style={{ fontSize: 13, color: "var(--text-2)" }}>{c.role}</span>
            <StatusDot status={c.status} />
            <div style={{ textAlign: "right" }}>
              {c.score !== null ? (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 15, color: c.score >= 85 ? "var(--green)" : c.score >= 70 ? "var(--amber)" : "var(--red)" }}>{c.score}</span>
              ) : <span style={{ color: "var(--text-3)" }}>—</span>}
            </div>
            <div style={{ textAlign: "right", fontSize: 12.5, color: "var(--text-3)" }}>{c.time}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
              {c.status === "completed" && <Btn variant="ghost" small icon="eye" onClick={() => onViewResult(c)}>View</Btn>}
              {c.status !== "completed" && <Btn variant="ghost" small icon="refresh" onClick={() => onReset(c)} />}
              <Btn variant="ghost" small icon="trash" onClick={() => onDelete(c)} />
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};

// ── Result Detail Screen ──────────────────────────────────────────────────────
interface TranscriptMessage { role: 'user' | 'assistant'; content: string; timestamp: string; }
interface CodeAnnotation { line: number; comment: string; }

interface ResultDetailScreenProps { candidate: Candidate; onBack: () => void; }
const ResultDetailScreen: React.FC<ResultDetailScreenProps> = ({ candidate, onBack }) => {
  const [transcript, setTranscript] = useState<TranscriptMessage[] | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [submittedCode, setSubmittedCode] = useState<string>('');
  const [codeAnnotations, setCodeAnnotations] = useState<CodeAnnotation[]>([]);
  const [codeLoading, setCodeLoading] = useState(true);
  const [codeOpen, setCodeOpen] = useState(false);
  const [openAnnotation, setOpenAnnotation] = useState<number | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/sessions/${candidate.id}/transcript`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        setTranscript(d.messages ?? []);
        setSubmittedCode(d.submitted_code ?? '');
        setCodeAnnotations(d.code_annotations ?? []);
        setCodeLoading(false);
      })
      .catch(() => { setTranscript([]); setCodeLoading(false); });
  }, [candidate.id]);
  const g = candidate.grade;
  const score = candidate.score ?? 0;
  const scoreColor = score >= 85 ? "var(--green)" : score >= 70 ? "var(--amber)" : "var(--red)";
  const scoreBg = score >= 85 ? "var(--green-light)" : score >= 70 ? "var(--amber-light)" : "var(--red-light)";

  const metrics = g ? [
    { label: "Flaw Detection", value: g.flaw_detection, max: 40 },
    { label: "Code Quality", value: g.code_quality, max: 40 },
    { label: "Communication", value: g.communication, max: 20 },
  ] : [];

  return (
    <div style={{ padding: "24px 32px 40px", display: "flex", flexDirection: "column", gap: 24 }}>
      <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-3)", cursor: "pointer", alignSelf: "flex-start", background: "none", border: "none", fontFamily: "inherit" }}>
        <Icon name="chevronRight" size={13} color="var(--text-3)" />
        Back to results
      </button>

      {/* Header card */}
      <Card style={{ padding: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Avatar name={candidate.name} size={52} color={candidate.avatar_color} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: "var(--text)", letterSpacing: "-0.3px" }}>{candidate.name}</div>
            <div style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 2 }}>{candidate.role} · Assessed {candidate.date}</div>
          </div>
          {candidate.score !== null ? (
            <div style={{ textAlign: "center", padding: "12px 24px", background: scoreBg, borderRadius: "var(--radius)" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 40, fontWeight: 700, color: scoreColor, lineHeight: 1 }}>{candidate.score}</div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4, fontWeight: 600 }}>OVERALL SCORE</div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "12px 24px", background: "var(--bg)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 13, color: "var(--text-3)" }}>Grading…</div>
            </div>
          )}
          <div style={{ padding: "0 16px", textAlign: "center" }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "var(--text)" }}>{candidate.time || "—"}</div>
            <div style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 600 }}>TIME TAKEN</div>
          </div>
          {candidate.score !== null && (
            <Badge variant={score >= 70 ? "green" : "red"}>{score >= 70 ? "✓ Pass" : "✗ Fail"}</Badge>
          )}
        </div>
      </Card>

      {g ? (
        <>
          {/* Score breakdown */}
          <div style={{ display: "flex", gap: 16 }}>
            {metrics.map(m => {
              const pct = Math.round((m.value / m.max) * 100);
              return (
                <Card key={m.label} style={{ flex: 1, padding: "20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>{m.label}</div>
                  <div style={{ height: 6, background: "var(--bg-3)", borderRadius: 99, overflow: "hidden", marginBottom: 10 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: pct >= 85 ? "var(--green)" : pct >= 60 ? "var(--amber)" : "var(--red)", borderRadius: 99 }} />
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 22, color: "var(--text)" }}>
                    {m.value}<span style={{ fontSize: 13, fontWeight: 400, color: "var(--text-3)" }}>/{m.max}</span>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Summary */}
          {g.summary && (
            <Card>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                <Icon name="results" size={15} color="var(--text-3)" />
                <div style={{ fontWeight: 700, fontSize: 15 }}>Assessor Summary</div>
              </div>
              <div style={{ padding: "18px 24px", fontSize: 14, color: "var(--text-2)", lineHeight: 1.7 }}>{g.summary}</div>
            </Card>
          )}

          {/* Flaws caught / missed */}
          {(g.flaws_caught.length > 0 || g.flaws_missed.length > 0) && (
            <div style={{ display: "flex", gap: 16 }}>
              {g.flaws_caught.length > 0 && (
                <Card style={{ flex: 1 }}>
                  <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon name="shield" size={15} color="var(--green)" />
                    <div style={{ fontWeight: 700, fontSize: 15, color: "var(--green)" }}>Flaws Caught</div>
                    <Badge variant="green">{g.flaws_caught.length}</Badge>
                  </div>
                  <ul style={{ padding: "16px 24px", margin: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                    {g.flaws_caught.map((f, i) => (
                      <li key={i} style={{ display: "flex", gap: 10, fontSize: 13.5, color: "var(--text-2)" }}>
                        <span style={{ color: "var(--green)", flexShrink: 0, marginTop: 1 }}>✓</span>{f}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {g.flaws_missed.length > 0 && (
                <Card style={{ flex: 1 }}>
                  <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
                    <Icon name="alert" size={15} color="var(--amber)" />
                    <div style={{ fontWeight: 700, fontSize: 15, color: "var(--amber)" }}>Flaws Missed</div>
                    <Badge variant="amber">{g.flaws_missed.length}</Badge>
                  </div>
                  <ul style={{ padding: "16px 24px", margin: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                    {g.flaws_missed.map((f, i) => (
                      <li key={i} style={{ display: "flex", gap: 10, fontSize: 13.5, color: "var(--text-2)" }}>
                        <span style={{ color: "var(--amber)", flexShrink: 0, marginTop: 1 }}>!</span>{f}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}
        </>
      ) : (
        <Card style={{ padding: "40px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "var(--text-3)" }}>Grade not yet available. The assessor runs asynchronously — check back in a moment.</div>
        </Card>
      )}

      {/* Submitted Code */}
      <Card>
        <button
          onClick={() => setCodeOpen(v => !v)}
          style={{ width: "100%", padding: "18px 24px", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
        >
          <Icon name="code" size={15} color="var(--text-3)" />
          <div style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>Submitted Code</div>
          {codeAnnotations.length > 0 && (
            <Badge variant="amber">{codeAnnotations.length} annotation{codeAnnotations.length !== 1 ? 's' : ''}</Badge>
          )}
          <Icon name={codeOpen ? "chevronDown" : "chevronRight"} size={15} color="var(--text-3)" />
        </button>

        {codeOpen && (
          <div style={{ borderTop: "1px solid var(--border)" }}>
            {codeLoading ? (
              <div style={{ padding: "24px", textAlign: "center", fontSize: 13.5, color: "var(--text-3)" }}>Loading…</div>
            ) : !submittedCode ? (
              <div style={{ padding: "24px", textAlign: "center", fontSize: 13.5, color: "var(--text-3)" }}>No code submitted.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>
                  <tbody>
                    {submittedCode.split('\n').map((line, idx) => {
                      const lineNum = idx + 1;
                      const annotation = codeAnnotations.find(a => a.line === lineNum);
                      const isOpen = openAnnotation === lineNum;
                      return (
                        <React.Fragment key={idx}>
                          <tr style={{ background: isOpen ? "var(--amber-light)" : annotation ? "rgba(251,191,36,0.06)" : undefined }}>
                            <td style={{ width: 44, paddingRight: 12, textAlign: "right", color: "var(--text-3)", userSelect: "none", borderRight: "1px solid var(--border)", paddingLeft: 16, paddingTop: 1, paddingBottom: 1, verticalAlign: "top", fontSize: 11 }}>
                              {lineNum}
                            </td>
                            <td style={{ paddingLeft: 16, paddingTop: 1, paddingBottom: 1, whiteSpace: "pre", color: "var(--text-2)", width: "100%" }}>
                              {line || ' '}
                            </td>
                            <td style={{ width: 32, paddingRight: 8, textAlign: "center", verticalAlign: "top" }}>
                              {annotation && (
                                <button
                                  onClick={() => setOpenAnnotation(isOpen ? null : lineNum)}
                                  title={annotation.comment}
                                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "1px 2px", opacity: 0.85 }}
                                >
                                  💬
                                </button>
                              )}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr style={{ background: "var(--amber-light)" }}>
                              <td />
                              <td colSpan={2} style={{ padding: "6px 16px 10px", fontSize: 12.5, color: "var(--amber)", fontFamily: "inherit", lineHeight: 1.5, borderBottom: "1px solid var(--border)" }}>
                                {annotation!.comment}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* AI Conversation Transcript */}
      <Card>
        <button
          onClick={() => setTranscriptOpen(v => !v)}
          style={{ width: "100%", padding: "18px 24px", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
        >
          <Icon name="code" size={15} color="var(--text-3)" />
          <div style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>AI Conversation Transcript</div>
          {transcript !== null && (
            <Badge variant="default">{transcript.length} messages</Badge>
          )}
          <Icon name={transcriptOpen ? "chevronDown" : "chevronRight"} size={15} color="var(--text-3)" />
        </button>

        {transcriptOpen && (
          <div style={{ borderTop: "1px solid var(--border)", maxHeight: 520, overflowY: "auto" }}>
            {transcript === null ? (
              <div style={{ padding: "24px", textAlign: "center", fontSize: 13.5, color: "var(--text-3)" }}>Loading…</div>
            ) : transcript.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", fontSize: 13.5, color: "var(--text-3)" }}>No messages recorded.</div>
            ) : transcript.map((msg, i) => (
              <div key={i} style={{
                padding: "14px 24px",
                borderBottom: i < transcript.length - 1 ? "1px solid var(--border)" : "none",
                background: msg.role === 'assistant' ? "var(--bg)" : "#fff",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
                    color: msg.role === 'assistant' ? "var(--accent)" : "var(--text-3)",
                  }}>
                    {msg.role === 'assistant' ? 'AI Assistant' : 'Candidate'}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: 13.5, color: "var(--text-2)", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

// ── Template Form Modal ───────────────────────────────────────────────────────
const CATEGORIES = ["Full-Stack", "Backend", "Frontend", "ML / AI", "DevOps", "Data", "Algorithms", "Other"];
const LEVELS: AssessmentTemplate["level"][] = ["Junior", "Mid", "Senior"];

interface TemplateFormModalProps {
  initial?: AssessmentTemplate | null;
  onSave: (t: AssessmentTemplate) => void;
  onClose: () => void;
}
const TemplateFormModal: React.FC<TemplateFormModalProps> = ({ initial, onSave, onClose }) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState(initial?.category ?? CATEGORIES[0]);
  const [level, setLevel] = useState<AssessmentTemplate["level"]>(initial?.level ?? "Mid");
  const [difficulty, setDifficulty] = useState(initial?.difficulty ?? 3);
  const [challenge, setChallenge] = useState(initial?.challenge ?? "");
  const [challengeTab, setChallengeTab] = useState<"write" | "preview">("write");
  const isEdit = !!initial;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      id: initial?.id ?? String(Date.now()),
      name: name.trim(),
      type: "custom",
      category,
      level,
      difficulty,
      challenge,
      uses: initial?.uses ?? 0,
      avgScore: initial?.avgScore ?? 0,
    });
  };

  const inputStyle: React.CSSProperties = { width: "100%", padding: "9px 12px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", fontSize: 13.5, fontFamily: "inherit", outline: "none", background: "var(--bg)" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "oklch(0% 0 0 / 0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16, overflowY: "auto" }}>
      <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 8px 32px oklch(0% 0 0 / 0.14)", width: "100%", maxWidth: 640, padding: 28, margin: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{isEdit ? "Edit Template" : "New Template"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 4, display: "flex", borderRadius: 6 }}><Icon name="x" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-2)" }}>Template Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Senior Backend Engineer" required style={inputStyle} autoFocus />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-2)" }}>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-2)" }}>Level</label>
              <select value={level} onChange={e => setLevel(e.target.value as AssessmentTemplate["level"])} style={inputStyle}>
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-2)" }}>Difficulty</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[1,2,3,4,5].map(d => (
                <button key={d} type="button" onClick={() => setDifficulty(d)}
                  style={{ flex: 1, padding: "8px 0", borderRadius: "var(--radius-sm)", border: `1.5px solid ${difficulty === d ? "var(--accent)" : "var(--border)"}`, background: difficulty === d ? "var(--accent-light)" : "#fff", color: difficulty === d ? "var(--accent)" : "var(--text-3)", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", transition: "all 0.12s" }}>
                  {d}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
              {["", "Straightforward", "Moderate", "Challenging", "Hard", "Expert"][difficulty]}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-2)" }}>Challenge Description <span style={{ fontWeight: 400, color: "var(--text-3)" }}>(Markdown)</span></label>
              <div style={{ display: "flex", background: "var(--bg-3)", borderRadius: 6, padding: 2, gap: 2 }}>
                {(["write", "preview"] as const).map(tab => (
                  <button key={tab} type="button" onClick={() => setChallengeTab(tab)}
                    style={{ padding: "3px 12px", borderRadius: 5, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: challengeTab === tab ? "#fff" : "transparent", color: challengeTab === tab ? "var(--text-1)" : "var(--text-3)", boxShadow: challengeTab === tab ? "0 1px 3px oklch(0% 0 0 / 0.08)" : "none", transition: "all 0.12s" }}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {challengeTab === "write" ? (
              <textarea
                value={challenge}
                onChange={e => setChallenge(e.target.value)}
                placeholder={`## The Challenge\n\nDescribe the problem the candidate needs to solve...\n\n## Requirements\n\n- Requirement 1\n- Requirement 2\n\n## Evaluation Criteria\n\n- Correctness\n- Code quality`}
                rows={12}
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
              />
            ) : (
              <div style={{ minHeight: 200, padding: "12px 14px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "var(--bg)", fontSize: 13.5, lineHeight: 1.7, color: "var(--text-1)", overflowY: "auto" }}>
                {challenge ? (
                  <MarkdownPreview src={challenge} />
                ) : (
                  <span style={{ color: "var(--text-3)", fontStyle: "italic" }}>Nothing to preview yet.</span>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
            <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn type="submit">{isEdit ? "Save Changes" : "Create Template"}</Btn>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Delete Template Modal ─────────────────────────────────────────────────────
interface DeleteTemplateModalProps {
  template: AssessmentTemplate;
  onConfirm: () => void;
  onClose: () => void;
}
const DeleteTemplateModal: React.FC<DeleteTemplateModalProps> = ({ template, onConfirm, onClose }) => {
  const isPlatform = template.type === "fixed";
  return (
    <div style={{ position: "fixed", inset: 0, background: "oklch(0% 0 0 / 0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 8px 32px oklch(0% 0 0 / 0.14)", width: "100%", maxWidth: 420, padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Remove Template</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 4, display: "flex", borderRadius: 6 }}><Icon name="x" size={18} /></button>
        </div>
        <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.6, marginBottom: 8 }}>
          Are you sure you want to remove <strong style={{ color: "var(--text)" }}>{template.name}</strong>?
        </p>
        {isPlatform ? (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--bg)", border: "1px solid var(--border)", fontSize: 13, color: "var(--text-3)", marginBottom: 20 }}>
            This is a TuringScout platform template. It will be <strong>hidden from your view</strong> but not deleted — you can contact support to restore it.
          </div>
        ) : (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "oklch(98% 0.02 25)", border: "1px solid oklch(90% 0.04 25)", fontSize: 13, color: "var(--red)", marginBottom: 20 }}>
            This is your custom template and will be <strong>permanently deleted</strong>.
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm}>{isPlatform ? "Hide Template" : "Delete Template"}</Btn>
        </div>
      </div>
    </div>
  );
};

// ── Assessments Screen ────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.turingscout.com';
const getToken = () => sessionStorage.getItem("ts_portal_token") || "";
const authHeaders = () => ({ "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` });

function normalise(t: any): AssessmentTemplate {
  return { ...t, id: t.template_id ?? t.id, avgScore: t.avg_score ?? t.avgScore ?? 0 };
}

interface AssessmentsScreenProps { onInvite: (t: AssessmentTemplate) => void; }
const AssessmentsScreen: React.FC<AssessmentsScreenProps> = ({ onInvite }) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<keyof AssessmentTemplate>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(0);
  const [diffFilter, setDiffFilter] = useState(0);
  const [catFilter, setCatFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [templates, setTemplates] = useState<AssessmentTemplate[]>(ALL_TEMPLATES);
  const [formModal, setFormModal] = useState<{ open: boolean; template: AssessmentTemplate | null }>({ open: false, template: null });
  const [deleteTarget, setDeleteTarget] = useState<AssessmentTemplate | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/templates`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setTemplates((data.templates || []).map(normalise)))
      .catch(() => {/* keep static fallback */});
  }, []);

  const toggleSort = (key: keyof AssessmentTemplate) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon: React.FC<{ col: string }> = ({ col }) => {
    if (sortKey !== col) return <span style={{ opacity: 0.25, fontSize: 10 }}>↕</span>;
    return <span style={{ fontSize: 10, color: "var(--accent)" }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  const colHd = (label: string, key: keyof AssessmentTemplate) => (
    <span onClick={() => toggleSort(key)} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, userSelect: "none" }}>
      {label} <SortIcon col={key} />
    </span>
  );

  const handleSaveTemplate = async (t: AssessmentTemplate) => {
    const isEdit = templates.some(x => x.id === t.id && x.account_id !== "platform");
    try {
      let saved: AssessmentTemplate;
      if (isEdit) {
        const res = await fetch(`${API_BASE}/templates/${t.id}`, {
          method: "PATCH", headers: authHeaders(), body: JSON.stringify(t),
        });
        saved = normalise(await res.json());
        setTemplates(prev => prev.map(x => x.id === saved.id ? saved : x));
      } else {
        const res = await fetch(`${API_BASE}/templates`, {
          method: "POST", headers: authHeaders(), body: JSON.stringify(t),
        });
        saved = normalise(await res.json());
        setTemplates(prev => [...prev, saved]);
      }
    } catch { /* optimistic already applied if needed */ }
    setFormModal({ open: false, template: null });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${API_BASE}/templates/${deleteTarget.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      setTemplates(prev => prev.filter(x => x.id !== deleteTarget!.id));
    } catch { /* ignore */ }
    setDeleteTarget(null);
  };

  const categories = Array.from(new Set(templates.map(t => t.category))).sort();

  const filtered = templates
    .filter(t => {
      const q = search.toLowerCase();
      const matchSearch = !q || t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q) || t.level.toLowerCase().includes(q);
      const matchDiff = diffFilter === 0 || t.difficulty === diffFilter;
      const matchCat = !catFilter || t.category === catFilter;
      const matchLevel = !levelFilter || t.level === levelFilter;
      const matchSource = !sourceFilter || t.type === sourceFilter;
      return matchSearch && matchDiff && matchCat && matchLevel && matchSource;
    })
    .sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const aStr = typeof av === "string" ? av.toLowerCase() : av;
      const bStr = typeof bv === "string" ? bv.toLowerCase() : bv;
      if (aStr === undefined || bStr === undefined) return 0;
      return sortDir === "asc" ? (aStr > bStr ? 1 : -1) : (aStr < bStr ? 1 : -1);
    });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  useEffect(() => { setPage(0); }, [search, diffFilter, catFilter, levelFilter, sourceFilter, sortKey, sortDir, pageSize]);

  return (
    <div style={{ padding: "24px 32px 40px" }}>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Assessment Templates</div>
          <Btn icon="plus" small onClick={() => setFormModal({ open: true, template: null })}>New Template</Btn>
        </div>

        {/* ── Filter bar row 1: search + dropdowns ── */}
        <div style={{ padding: "12px 24px 0", borderBottom: "none", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "var(--bg)" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 180, maxWidth: 280 }}>
            <div style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <Icon name="search" size={13} color="var(--text-3)" />
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates…"
              style={{ width: "100%", padding: "7px 10px 7px 30px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13, background: "#fff", outline: "none", fontFamily: "inherit" }} />
          </div>

          {/* Category dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 500, whiteSpace: "nowrap" }}>Category:</span>
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
              style={{ padding: "6px 24px 6px 9px", border: `1px solid ${catFilter ? "var(--accent)" : "var(--border)"}`, borderRadius: "var(--radius-sm)", fontSize: 12.5, background: catFilter ? "var(--accent-light)" : "#fff", color: catFilter ? "var(--accent)" : "var(--text-2)", fontWeight: catFilter ? 600 : 400, outline: "none", fontFamily: "inherit", cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239ca3af'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
              <option value="">All</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Source dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 500, whiteSpace: "nowrap" }}>Source:</span>
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
              style={{ padding: "6px 24px 6px 9px", border: `1px solid ${sourceFilter ? "var(--accent)" : "var(--border)"}`, borderRadius: "var(--radius-sm)", fontSize: 12.5, background: sourceFilter ? "var(--accent-light)" : "#fff", color: sourceFilter ? "var(--accent)" : "var(--text-2)", fontWeight: sourceFilter ? 600 : 400, outline: "none", fontFamily: "inherit", cursor: "pointer", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239ca3af'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
              <option value="">All</option>
              <option value="fixed">Platform</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Active-filter reset */}
          {(catFilter || levelFilter || sourceFilter || diffFilter !== 0) && (
            <button onClick={() => { setCatFilter(""); setLevelFilter(""); setSourceFilter(""); setDiffFilter(0); }}
              style={{ padding: "5px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--red)", background: "var(--red-light)", color: "var(--red)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              Clear filters ×
            </button>
          )}

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>Show:</span>
            {[5, 10, 25].map(n => (
              <button key={n} onClick={() => setPageSize(n)}
                style={{ padding: "4px 9px", borderRadius: "var(--radius-sm)", fontSize: 12, fontWeight: 600, border: `1px solid ${pageSize === n ? "var(--accent)" : "var(--border)"}`, background: pageSize === n ? "var(--accent-light)" : "#fff", color: pageSize === n ? "var(--accent)" : "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* ── Filter bar row 2: Level + Difficulty pills ── */}
        <div style={{ padding: "10px 24px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", background: "var(--bg)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 500, whiteSpace: "nowrap" }}>Level:</span>
            {["", "Junior", "Mid", "Senior"].map(l => (
              <button key={l} onClick={() => setLevelFilter(l)}
                style={{ padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600, border: `1px solid ${levelFilter === l ? "var(--accent)" : "var(--border)"}`, background: levelFilter === l ? "var(--accent-light)" : "#fff", color: levelFilter === l ? "var(--accent)" : "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>
                {l || "All"}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "var(--text-3)", fontWeight: 500, whiteSpace: "nowrap" }}>Difficulty:</span>
            {[0,1,2,3,4,5].map(d => (
              <button key={d} onClick={() => setDiffFilter(d)}
                style={{ padding: "3px 9px", borderRadius: 99, fontSize: 12, fontWeight: 600, border: `1px solid ${diffFilter === d ? "var(--accent)" : "var(--border)"}`, background: diffFilter === d ? "var(--accent-light)" : "#fff", color: diffFilter === d ? "var(--accent)" : "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>
                {d === 0 ? "All" : d}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: "10px 24px", display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 90px 64px 72px 110px", gap: 8, fontSize: 11.5, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid var(--border)", background: "var(--bg)" }}>
          {colHd("Template", "name")}
          {colHd("Category", "category")}
          {colHd("Level", "level")}
          {colHd("Difficulty", "difficulty")}
          <span style={{ textAlign: "right" }}>{colHd("Uses", "uses")}</span>
          <span style={{ textAlign: "right" }}>{colHd("Avg Score", "avgScore")}</span>
          <span></span>
        </div>

        {pageItems.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: "var(--text-3)", fontSize: 13.5 }}>No templates match your search.</div>
        ) : pageItems.map((t, i) => (
          <div key={t.id} style={{ padding: "13px 24px", display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 90px 64px 72px 110px", gap: 8, alignItems: "center", borderBottom: i < pageItems.length - 1 ? "1px solid var(--border)" : "none", transition: "background 0.1s" }}
            onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = "var(--bg)"}
            onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = ""}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <Icon name="code" size={14} color="var(--text-3)" />
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                <Badge variant={t.type === "fixed" ? "default" : "accent"}>{t.type === "fixed" ? "Fixed" : "Custom"}</Badge>
              </div>
            </div>
            <span style={{ fontSize: 13, color: "var(--text-2)" }}>{t.category}</span>
            <Badge variant={t.level === "Junior" ? "teal" : t.level === "Mid" ? "default" : "accent"}>{t.level}</Badge>
            <div style={{ display: "flex", gap: 3 }}>
              {[1,2,3,4,5].map(idx => (
                <span key={idx} style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: idx <= t.difficulty ? (t.difficulty >= 4 ? "var(--red)" : t.difficulty === 3 ? "var(--amber)" : "var(--green)") : "var(--bg-3)" }} />
              ))}
            </div>
            <span style={{ textAlign: "right", fontSize: 13, color: "var(--text-2)", fontFamily: "'JetBrains Mono', monospace" }}>{t.uses}</span>
            <span style={{ textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: (t.avgScore ?? 0) >= 80 ? "var(--green)" : (t.avgScore ?? 0) >= 65 ? "var(--amber)" : "var(--red)" }}>{t.avgScore ?? "—"}</span>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
              <Btn variant="secondary" small icon="send" onClick={() => onInvite(t)}>Send</Btn>
              <Btn variant="ghost" small icon="edit" onClick={() => setFormModal({ open: true, template: t })}></Btn>
              <Btn variant="ghost" small icon="trash" onClick={() => setDeleteTarget(t)}></Btn>
            </div>
          </div>
        ))}

        <div style={{ padding: "12px 24px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg)" }}>
          <span style={{ fontSize: 12.5, color: "var(--text-3)" }}>
            {filtered.length === 0 ? "No results" : `${page * pageSize + 1}–${Math.min(page * pageSize + pageSize, filtered.length)} of ${filtered.length} templates`}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ padding: "5px 12px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "#fff", fontSize: 12.5, fontWeight: 600, color: page === 0 ? "var(--text-3)" : "var(--text-2)", cursor: page === 0 ? "default" : "pointer", opacity: page === 0 ? 0.5 : 1, fontFamily: "inherit" }}>← Prev</button>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <button key={idx} onClick={() => setPage(idx)}
                style={{ padding: "5px 10px", borderRadius: "var(--radius-sm)", border: `1px solid ${idx === page ? "var(--accent)" : "var(--border)"}`, background: idx === page ? "var(--accent-light)" : "#fff", fontSize: 12.5, fontWeight: 600, color: idx === page ? "var(--accent)" : "var(--text-2)", cursor: "pointer", fontFamily: "inherit" }}>{idx + 1}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              style={{ padding: "5px 12px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "#fff", fontSize: 12.5, fontWeight: 600, color: page >= totalPages - 1 ? "var(--text-3)" : "var(--text-2)", cursor: page >= totalPages - 1 ? "default" : "pointer", opacity: page >= totalPages - 1 ? 0.5 : 1, fontFamily: "inherit" }}>Next →</button>
          </div>
        </div>
      </Card>

      {formModal.open && (
        <TemplateFormModal
          initial={formModal.template}
          onSave={handleSaveTemplate}
          onClose={() => setFormModal({ open: false, template: null })}
        />
      )}
      {deleteTarget && (
        <DeleteTemplateModal
          template={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

// ── Send Assessment Modal ─────────────────────────────────────────────────────
interface SendAssessmentModalProps { template: AssessmentTemplate; onClose: () => void; onSent: (s: Candidate) => void; }
const SendAssessmentModal: React.FC<SendAssessmentModalProps> = ({ template, onClose, onSent }) => {
  const [form, setForm] = useState({ name: "", email: "", role: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState("");

  const handleSend = async () => {
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/sessions`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          candidate_name: form.name.trim(),
          candidate_email: form.email.trim().toLowerCase(),
          role: form.role.trim(),
          template_id: template.id,
          challenge_title: template.name,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const session = await res.json();
      setLink(session.invite_url ?? "");
      onSent(normaliseSession(session));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "oklch(0% 0 0 / 0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "var(--shadow-lg)", width: 500, maxWidth: "calc(100vw - 32px)", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)" }}>Send Assessment</div>
            <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2 }}>{template.name}</div>
          </div>
          <button onClick={onClose} style={{ padding: 6, borderRadius: 6, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer" }}><Icon name="x" size={16} /></button>
        </div>

        {link ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: 99, background: "var(--green-light)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="check" size={22} color="var(--green)" />
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)" }}>Assessment Sent!</div>
            <div style={{ fontSize: 13.5, color: "var(--text-3)", marginTop: 6, marginBottom: 20 }}>Share this link with <strong>{form.name}</strong> to begin their assessment.</div>
            <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
              <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: "var(--text-2)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link}</span>
              <button onClick={() => navigator.clipboard.writeText(link)} style={{ padding: 4, color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}><Icon name="copy" size={14} /></button>
            </div>
            <Btn onClick={onClose}>Done</Btn>
          </div>
        ) : (
          <div style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--accent-light)", border: "1px solid oklch(87% 0.09 265)", borderRadius: "var(--radius-sm)" }}>
              <Icon name="code" size={14} color="var(--accent)" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--accent)" }}>{template.name}</div>
                <div style={{ fontSize: 12, color: "var(--text-3)" }}>{template.category} · {template.level} · Difficulty {template.difficulty}/5</div>
              </div>
            </div>
            {(["name", "email", "role"] as const).map(key => (
              <div key={key}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                  {key === "name" ? "Candidate Full Name" : key === "email" ? "Email Address" : "Role / Position Applied For"}
                </label>
                <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  type={key === "email" ? "email" : "text"}
                  placeholder={key === "name" ? "e.g. Alex Johnson" : key === "email" ? "candidate@email.com" : "e.g. Senior Backend Engineer"}
                  autoFocus={key === "name"}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
              </div>
            ))}
            {error && <div style={{ fontSize: 13, color: "var(--red)", padding: "8px 12px", background: "var(--red-light)", borderRadius: "var(--radius-sm)" }}>{error}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
              <Btn icon="send" disabled={sending || !form.name || !form.email || !form.role} onClick={handleSend}>{sending ? "Sending…" : "Send Assessment"}</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Profile Screen ────────────────────────────────────────────────────────────
interface ProfileScreenProps { user: { name: string; email: string }; role: PortalRole; }
const ProfileScreen: React.FC<ProfileScreenProps> = ({ user, role }) => {
  const [tab, setTab] = useState("profile");
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ name: user.name, email: user.email, title: "Senior Technical Recruiter", company: "Acme Corp" });

  return (
    <div style={{ padding: "24px 32px 40px", display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border)", marginBottom: 0 }}>
        {["profile", "security", "notifications"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: "10px 20px", fontSize: 13.5, fontWeight: 600, color: tab === t ? "var(--accent)" : "var(--text-3)", background: "none", border: "none", borderBottom: `2px solid ${tab === t ? "var(--accent)" : "transparent"}`, cursor: "pointer", textTransform: "capitalize", transition: "all 0.12s", fontFamily: "inherit" }}>
            {t === "security" ? "Password & Security" : t === "notifications" ? "Notifications" : "Profile"}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <Card style={{ padding: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid var(--border)" }}>
            <Avatar name={user.name} size={64} color={role === "admin" || role === "superadmin" ? "oklch(55% 0.18 295)" : "var(--accent)"} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)" }}>{user.name}</div>
              <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 2 }}>{user.email}</div>
              <div style={{ marginTop: 8 }}><Badge variant="accent">{role === "admin" || role === "superadmin" ? "Admin" : "Recruiter"}</Badge></div>
            </div>
            <Btn variant="secondary" small>Change Photo</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {([{ key: "name", label: "Full Name" }, { key: "email", label: "Email Address" }, { key: "title", label: "Job Title" }, { key: "company", label: "Company" }] as {key: keyof typeof form; label: string}[]).map(f => (
              <div key={f.key}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>{f.label}</label>
                <input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, display: "flex", gap: 8 }}>
            <Btn onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2000); }}>{saved ? "✓ Saved!" : "Save Changes"}</Btn>
            <Btn variant="secondary">Cancel</Btn>
          </div>
        </Card>
      )}

      {tab === "security" && (
        <Card style={{ padding: 28 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Change Password</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 380 }}>
            {["Current Password", "New Password", "Confirm New Password"].map(label => (
              <div key={label}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>{label}</label>
                <input type="password" placeholder="••••••••"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
              </div>
            ))}
            <Btn icon="key">Update Password</Btn>
          </div>
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Two-Factor Authentication</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>Authenticator App</div>
                <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2 }}>Not configured</div>
              </div>
              <Btn variant="secondary" small icon="shield">Enable 2FA</Btn>
            </div>
          </div>
        </Card>
      )}

      {tab === "notifications" && (
        <Card style={{ padding: 28 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Email Notifications</div>
          {[
            { label: "Assessment Completed", desc: "When a candidate finishes their session", on: true },
            { label: "Invitation Accepted", desc: "When a candidate opens their invite link", on: true },
            { label: "Weekly Summary", desc: "A digest of activity across your account", on: false },
            { label: "Score Alerts", desc: "When a candidate scores above 90 or below 50", on: true },
          ].map(item => (
            <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.label}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2 }}>{item.desc}</div>
              </div>
              <div style={{ width: 36, height: 20, borderRadius: 99, background: item.on ? "var(--accent)" : "var(--border)", cursor: "pointer", position: "relative", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 3, left: item.on ? 19 : 3, width: 14, height: 14, borderRadius: 99, background: "#fff", transition: "left 0.15s" }} />
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

// ── Configuration Screen ──────────────────────────────────────────────────────
interface ConfigurationScreenProps { role: PortalRole; }
const ConfigurationScreen: React.FC<ConfigurationScreenProps> = ({ role }) => {
  const [activeSection, setActiveSection] = useState("general");
  const sections = [
    { id: "general", label: "General", icon: "settings" },
    { id: "integrations", label: "Integrations", icon: "cpu" },
    ...(role === "admin" || role === "superadmin" ? [
      { id: "users", label: "Users & Roles", icon: "profile" },
    ] : []),
  ];

  return (
    <div style={{ padding: "24px 32px 40px", display: "flex", gap: 24 }}>
      {/* Sidebar nav */}
      <div style={{ width: 200, flexShrink: 0 }}>
        <Card style={{ padding: "8px" }}>
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", borderRadius: "var(--radius-sm)", fontSize: 13.5, fontWeight: 500, background: activeSection === s.id ? "var(--accent-light)" : "transparent", color: activeSection === s.id ? "var(--accent)" : "var(--text-2)", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
              <Icon name={s.icon} size={15} color={activeSection === s.id ? "var(--accent)" : "var(--text-3)"} />
              {s.label}
            </button>
          ))}
        </Card>
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        {activeSection === "general" && (
          <Card style={{ padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>General Settings</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {[{ label: "Organization Name", val: "Acme Corp" }, { label: "Support Email", val: "talent@acme.com" }, { label: "Default Invite Expiry (days)", val: "7" }].map(f => (
                <div key={f.label}>
                  <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>{f.label}</label>
                  <input defaultValue={f.val} style={{ width: "100%", maxWidth: 380, padding: "9px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 13.5, background: "var(--bg)", outline: "none", fontFamily: "inherit" }} />
                </div>
              ))}
              <div style={{ marginTop: 8 }}><Btn>Save Settings</Btn></div>
            </div>
          </Card>
        )}

        {activeSection === "integrations" && (
          <Card style={{ padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Integrations</div>
            {[
              { name: "Slack", desc: "Receive notifications in your workspace when assessments complete", connected: false },
              { name: "Greenhouse", desc: "ATS sync — push results directly to your hiring pipeline", connected: false },
              { name: "Lever", desc: "Sync candidate scores and reports with Lever", connected: false },
              { name: "Workday", desc: "Push assessment results into Workday Recruiting", connected: false },
            ].map(int => (
              <div key={int.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{int.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2 }}>{int.desc}</div>
                </div>
                <Badge variant={int.connected ? "green" : "default"}>{int.connected ? "Connected" : "Coming soon"}</Badge>
              </div>
            ))}
          </Card>
        )}

        {activeSection === "users" && (role === "admin" || role === "superadmin") && (
          <Card style={{ padding: 28 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Users & Roles</div>
              <Btn icon="plus" small>Invite User</Btn>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 16 }}>
              <strong>Admins</strong> can manage recruiters and configuration. <strong>Recruiters</strong> can send assessments and view results.
            </div>
            {[
              { name: "David Park", email: "david@acme.com", role: "admin", color: "oklch(55% 0.18 295)" },
              { name: "Emma Wilson", email: "emma@acme.com", role: "recruiter", color: "var(--accent)" },
              { name: "Tom Nakamura", email: "tom@acme.com", role: "recruiter", color: "var(--amber)" },
            ].map(u => (
              <div key={u.email} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
                <Avatar name={u.name} size={32} color={u.color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{u.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{u.email}</div>
                </div>
                <select defaultValue={u.role} style={{ padding: "5px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", fontSize: 12.5, background: "var(--bg)", color: "var(--text)", cursor: "pointer", fontFamily: "inherit" }}>
                  <option value="recruiter">Recruiter</option>
                  <option value="admin">Admin</option>
                </select>
                <Btn variant="ghost" small icon="trash"></Btn>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
};

// ── Portal Page ───────────────────────────────────────────────────────────────
const Portal: React.FC = () => {
  const navigate = useNavigate();

  const storedUser: PortalUser | null = (() => {
    try { return JSON.parse(sessionStorage.getItem("ts_portal_user") || "null"); } catch { return null; }
  })();

  const [screen, setScreen] = useState("dashboard");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteTemplate, setInviteTemplate] = useState<AssessmentTemplate | null>(null);
  const [sendTemplate, setSendTemplate] = useState<AssessmentTemplate | null>(null);
  const [selectedResult, setSelectedResult] = useState<Candidate | null>(null);
  const [sessions, setSessions] = useState<Candidate[]>([]);
  const [stats, setStats] = useState<DashStats | null>(null);
  const [templates, setTemplates] = useState<AssessmentTemplate[]>(ALL_TEMPLATES);

  useEffect(() => {
    if (!storedUser || !sessionStorage.getItem("ts_portal_token")) { navigate("/login", { replace: true }); return; }
    // Fetch sessions
    fetch(`${API_BASE}/sessions`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setSessions((d.sessions ?? []).map(normaliseSession)))
      .catch(() => {});
    // Fetch stats
    fetch(`${API_BASE}/sessions/stats`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setStats(d))
      .catch(() => {});
    // Fetch templates
    fetch(`${API_BASE}/templates`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setTemplates((d.templates ?? []).map(normalise)))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [deleteTarget, setDeleteTarget] = useState<Candidate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState<Candidate | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetUrl, setResetUrl] = useState<string | null>(null);
  const [copyTick, setCopyTick] = useState(false);

  const role: PortalRole = (storedUser?.role as PortalRole) ?? "recruiter";
  const user = { name: storedUser?.name ?? "", email: storedUser?.email ?? "" };

  if (!storedUser) return null;

  const screenTitles: Record<string, { title: string; subtitle: string }> = {
    dashboard: { title: "Dashboard", subtitle: `Welcome back, ${user.name.split(" ")[0]}.` },
    candidates: { title: "Candidates", subtitle: `${sessions.length} total candidate${sessions.length !== 1 ? "s" : ""}` },
    results: { title: "Results", subtitle: "Review completed assessments" },
    assessments: { title: "Assessments", subtitle: "Manage templates and configure challenges" },
    configuration: { title: "Configuration", subtitle: "Manage your account settings" },
    profile: { title: "My Account", subtitle: "Manage your profile and preferences" },
  };

  const currentScreen = screen === "results" && selectedResult ? null : screen;
  const info = currentScreen ? screenTitles[currentScreen] : null;

  const handleViewResult = (candidate: Candidate) => {
    setSelectedResult(candidate);
    setScreen("results");
  };

  const handleSessionSent = (newSession: Candidate) => {
    setSessions(prev => [newSession, ...prev]);
    setStats(prev => prev ? { ...prev, total: prev.total + 1, active: prev.active + 1 } : null);
  };

  const handleDeleteSession = async () => {
    if (!deleteTarget || deleteLoading) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_BASE}/sessions/${deleteTarget.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.id !== deleteTarget.id));
        setStats(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : null);
      }
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleResetInvite = async () => {
    if (!resetTarget || resetLoading) return;
    setResetLoading(true);
    try {
      const res = await fetch(`${API_BASE}/sessions/${resetTarget.id}/reset`, {
        method: "POST", headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setResetUrl(data.invite_url);
        setSessions(prev => prev.map(s => s.id === resetTarget.id ? { ...s, status: "pending" } : s));
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("ts_portal_token");
    sessionStorage.removeItem("ts_portal_user");
    navigate('/login');
  };

  return (
    <div style={{
      display: "flex",
      width: "100%",
      height: "100vh",
      overflow: "hidden",
      fontFamily: "'Inter', sans-serif",
      fontSize: 14,
      lineHeight: 1.5,
      color: "var(--text)",
    }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --bg: oklch(98.5% 0.005 260);
          --bg-2: oklch(96% 0.008 260);
          --bg-3: oklch(93% 0.01 260);
          --border: oklch(88% 0.012 260);
          --border-strong: oklch(80% 0.015 260);
          --text: oklch(16% 0.015 260);
          --text-2: oklch(42% 0.02 260);
          --text-3: oklch(62% 0.015 260);
          --accent: oklch(52% 0.22 265);
          --accent-light: oklch(95% 0.06 265);
          --accent-hover: oklch(46% 0.22 265);
          --teal: oklch(55% 0.15 195);
          --teal-light: oklch(95% 0.05 195);
          --green: oklch(55% 0.15 145);
          --green-light: oklch(95% 0.05 145);
          --amber: oklch(68% 0.16 75);
          --amber-light: oklch(97% 0.05 75);
          --red: oklch(55% 0.18 25);
          --red-light: oklch(96% 0.05 25);
          --sidebar-w: 232px;
          --radius: 8px;
          --radius-sm: 5px;
          --shadow-sm: 0 1px 3px oklch(0% 0 0 / 0.06), 0 1px 2px oklch(0% 0 0 / 0.04);
          --shadow: 0 2px 8px oklch(0% 0 0 / 0.07), 0 1px 3px oklch(0% 0 0 / 0.05);
          --shadow-lg: 0 8px 24px oklch(0% 0 0 / 0.1), 0 2px 6px oklch(0% 0 0 / 0.06);
        }
        html, body { height: 100%; font-family: 'Inter', sans-serif; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 99px; }
      `}</style>

      <Sidebar
        activeScreen={screen}
        onNavigate={(s) => { setScreen(s); setSelectedResult(null); }}
        role={role}
        user={user}
        onLogout={handleLogout}
      />

      <main style={{ flex: 1, overflowY: "auto", background: "var(--bg)" }}>
        {info && (
          <Header title={info.title} subtitle={info.subtitle}
            actions={screen === "dashboard" ? (
              <button style={{ position: "relative", padding: 8, borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "#fff", cursor: "pointer" }}>
                <Icon name="bell" size={16} color="var(--text-3)" />
                <span style={{ position: "absolute", top: 5, right: 5, width: 7, height: 7, borderRadius: 99, background: "var(--accent)", border: "2px solid #fff" }} />
              </button>
            ) : undefined}
          />
        )}

        {screen === "results" && selectedResult ? (
          <ResultDetailScreen candidate={selectedResult} onBack={() => setSelectedResult(null)} />
        ) : screen === "dashboard" ? (
          <DashboardScreen onInvite={() => { setInviteTemplate(null); setShowInvite(true); }} onViewResult={handleViewResult} stats={stats} sessions={sessions} />
        ) : screen === "candidates" || screen === "results" ? (
          <CandidatesScreen onInvite={() => { setInviteTemplate(null); setShowInvite(true); }} onViewResult={handleViewResult} onDelete={c => setDeleteTarget(c)} onReset={c => { setResetTarget(c); setResetUrl(null); }} sessions={sessions} />
        ) : screen === "assessments" ? (
          <AssessmentsScreen onInvite={(tpl) => setSendTemplate(tpl)} />
        ) : screen === "configuration" ? (
          <ConfigurationScreen role={role} />
        ) : screen === "profile" ? (
          <ProfileScreen user={user} role={role} />
        ) : null}
      </main>

      {showInvite && (
        <InviteModal
          onClose={() => { setShowInvite(false); setInviteTemplate(null); }}
          onSent={(s) => { handleSessionSent(s); }}
          preselectedTemplate={inviteTemplate}
          templates={templates}
        />
      )}
      {sendTemplate && (
        <SendAssessmentModal
          template={sendTemplate}
          onClose={() => setSendTemplate(null)}
          onSent={(s) => { handleSessionSent(s); setSendTemplate(null); }}
        />
      )}

      {/* Delete candidate confirm modal */}
      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "var(--radius)", padding: 28, width: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)", marginBottom: 8 }}>Delete Candidate?</div>
            <div style={{ fontSize: 13.5, color: "var(--text-2)", marginBottom: 20 }}>
              This will permanently delete <strong>{deleteTarget.name}</strong>'s assessment session and all associated data. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDeleteSession} disabled={deleteLoading}>
                {deleteLoading ? "Deleting…" : "Delete"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Reset invite modal */}
      {resetTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "var(--radius)", padding: 28, width: 460, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--text)", marginBottom: 8 }}>
              {resetUrl ? "New Invite Link" : `Reset Invite for ${resetTarget.name}?`}
            </div>
            {!resetUrl ? (
              <>
                <div style={{ fontSize: 13.5, color: "var(--text-2)", marginBottom: 20 }}>
                  This will invalidate the existing invite link and generate a fresh one. The candidate's status will reset to <strong>Pending</strong>.
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <Btn variant="secondary" onClick={() => setResetTarget(null)}>Cancel</Btn>
                  <Btn onClick={handleResetInvite} disabled={resetLoading}>
                    {resetLoading ? "Resetting…" : "Reset Invite"}
                  </Btn>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13.5, color: "var(--text-2)", marginBottom: 12 }}>
                  Share this new link with {resetTarget.name}:
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--bg)", borderRadius: "var(--radius-sm)", padding: "10px 14px", border: "1px solid var(--border)", marginBottom: 20 }}>
                  <span style={{ fontSize: 12.5, color: "var(--text-2)", wordBreak: "break-all", flex: 1 }}>{resetUrl}</span>
                  <button onClick={() => { navigator.clipboard.writeText(resetUrl); setCopyTick(true); setTimeout(() => setCopyTick(false), 2000); }}
                    style={{ flexShrink: 0, padding: 4, color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}>
                    <Icon name={copyTick ? "check" : "copy"} size={14} />
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Btn onClick={() => { setResetTarget(null); setResetUrl(null); }}>Done</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Portal;
